// src/screens/admin/notifications/AdminNotificationsList.jsx
import React, { useContext, useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Swal from "sweetalert2";
import { MdNotifications, MdSearch, MdClose } from "react-icons/md";

// Reuse Components
import Pagination from "../../../components/general/pagination/Pagination";
import StatCard from "../../../components/admin/statcard/StatCard";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import axios from "axios";
// CSS
import "../../../assets/css/admin/notifications/AdminNotifications.css";
import AdminLayout from "../../../layouts/AdminLayout";
import MyContext from "../../../context/MyContext";
import { NotificationsPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import getEcho from "../../../components/general/echo/Echo";

function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const { baseUrl, authToken, loggedInAdmin } = useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Notifications");
  const [typeFilter, setTypeFilter] = useState("All Types");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);

  //----------API calling — GET + Bearer token (Topbar jaisa), admin
  // controller khud auth('Kmadmin')->user() se apni notifications nikalta hai
  const viewAllNotifications = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/notifications/viewAllNotifications`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setNotifications(response.data.notifications || []);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load notifications.",
        });
      });
  };

  const changeNotificationStatus = (notification) => {
    const formData = new FormData();
    formData.append("id", notification.id);

    axios
      .post(`${baseUrl}/admin/notifications/changeNotificationStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Notification status successfully updated!",
          });
          viewAllNotifications();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const deleteNotification = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This notification will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", id);
        axios
          .post(`${baseUrl}/admin/notifications/deleteNotification`, formData, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            if (response.data.success == true) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Notification deleted successfully!",
              });
              viewAllNotifications();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to delete notification.",
              });
            }
          })
          .catch((error) => {
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete notifications.",
            });
          });
      }
    });
  };

  useEffect(() => {
    if (!authToken) return;
    viewAllNotifications();
  }, [authToken]);

  // ── Pusher — naya notification aate hi list ke top pe turant dikhe,
  //    bina page refresh kiye ──
  useEffect(() => {
    if (!authToken || !loggedInAdmin?.id) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channel = echo.private(`admin.${loggedInAdmin.id}`);
    channel.listen(".new-notification", (data) => {
      setNotifications((prev) => [data.notification, ...prev]);
    });

    return () => {
      echo.leave(`admin.${loggedInAdmin.id}`);
    };
  }, [authToken, loggedInAdmin?.id, baseUrl]);

  // Stats Data — status database mein 'unread'/'Unread'/garbage bhi ho sakta
  // hai, is liye "read" ke elawa sab kuch unread mana jata hai
  const stats = [
    {
      label: "Total Notifications",
      value: notifications.length.toString(),
      icon: MdNotifications,
      iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      iconColor: "#2196f3",
      badge: "All",
    },
    {
      label: "Unread",
      value: notifications
        .filter((n) => String(n.status).toLowerCase() !== "read")
        .length.toString(),
      icon: MdNotifications,
      iconBg: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
      iconColor: "#ff9800",
      badge: "Pending",
    },
    {
      label: "Read",
      value: notifications.filter((n) => String(n.status).toLowerCase() === "read").length.toString(),
      icon: MdNotifications,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      iconColor: "#0ac46c",
      badge: "Done",
    },
  ];

  // Filters
  const filteredNotifications = notifications.filter((notification) => {
    const matchesSearch =
      notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All Notifications" ||
      (statusFilter === "Unread"
        ? String(notification.status).toLowerCase() !== "read"
        : String(notification.status).toLowerCase() === "read");
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNotifications = filteredNotifications.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  // ── Loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <AdminLayout>
        <NotificationsPageSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-notifications-page">
        <Container fluid>
          {/* Stats Cards */}
          <Row className="g-3 mb-4">
            {stats.map((stat, index) => (
              <Col key={index} xs={12} sm={6} lg={3}>
                <StatCard {...stat} />
              </Col>
            ))}
          </Row>

          {/* Filters */}
          <Row className="mb-3">
            <Col xs={12}>
              <div className="admin-filters-wrapper">
                <Row className="g-3 align-items-center">
                  <Col xs={12} md={8} lg={9}>
                    <div className="admin-search-box">
                      <span className="admin-search-icon">
                        <MdSearch />
                      </span>
                      <input
                        type="text"
                        className="admin-form-control"
                        placeholder="Search notifications..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={4} lg={3}>
                    <select
                      className="admin-form-control admin-select"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option>All Notifications</option>
                      <option>Unread</option>
                      <option>Read</option>
                    </select>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          {/* Notifications Table */}
          <Row>
            <Col xs={12}>
              <div className="admin-table-responsive">
                <table className="admin-table">
                  <thead className="admin-table-head">
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Message</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="admin-table-body">
                    {currentNotifications.map((n) => (
                      <tr
                        key={n.id}
                        className={
                          String(n.status).toLowerCase() !== "read" ? "admin-unread-row" : ""
                        }
                      >
                        <td>{n.id}</td>
                        <td>{n.title}</td>
                        <td className="admin-message-preview">{n.message}</td>
                        <td>{(n.created_at || n.date)?.split("T")[0]}</td>
                        <td>
                          {n.created_at
                            ? new Date(n.created_at).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                },
                              )
                            : ""}
                        </td>
                        <td>
                          <span
                            className={`admin-badge ${String(n.status).toLowerCase() === "read" ? "admin-badge-success" : "admin-badge-warning"}`}
                          >
                            {n.status}
                          </span>
                        </td>
                        <td>
                          <ActionDropdown
                            actions={[
                              {
                                label: "View",
                                icon: "MdVisibility",
                                onClick: () => {
                                  setSelectedNotification(n);
                                  setShowModal(true);
                                },
                                className: "view",
                              },
                              {
                                label:
                                  String(n.status).toLowerCase() === "read"
                                    ? "Mark as Unread"
                                    : "Mark as Read",
                                icon:
                                  String(n.status).toLowerCase() === "read"
                                    ? "MdMarkEmailUnread"
                                    : "MdMarkEmailRead",
                                onClick: () => {
                                  changeNotificationStatus(n);
                                },
                                className: "enable",
                              },
                              {
                                label: "Delete",
                                icon: "MdDelete",
                                onClick: () => {
                                  deleteNotification(n.id);
                                },
                                className: "delete",
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredNotifications.length === 0 && (
                  <div className="admin-no-data">No notifications found</div>
                )}
              </div>
            </Col>
          </Row>

          {/* Pagination */}
          <Row>
            <Col xs={12}>
              <Pagination
                totalItems={filteredNotifications.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </Col>
          </Row>
        </Container>

        {/* Notification Modal */}
        {showModal && selectedNotification && (
          <div
            className="admin-modal-overlay"
            onClick={() => setShowModal(false)}
          >
            <div
              className="admin-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="admin-modal-header">
                <h3 className="admin-modal-title">Notification Details</h3>
                <button
                  className="admin-modal-close"
                  onClick={() => setShowModal(false)}
                >
                  <MdClose />
                </button>
              </div>
              <div className="admin-modal-body">
                <p>
                  <strong>Title:</strong> {selectedNotification.title}
                </p>
                <p>
                  <strong>Message:</strong> {selectedNotification.message}
                </p>
                <p>
                  <strong>Date:</strong>{" "}
                  {selectedNotification.created_at?.split("T")[0] || ""} at
                  {selectedNotification.created_at
                    ? new Date(
                        selectedNotification.created_at,
                      ).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : ""}
                </p>
                <p>
                  <strong>Status:</strong> {selectedNotification.status}
                </p>
              </div>

              <div className="admin-modal-footer">
                {/* <button
                  className="custom-btn custom-btn-outline"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminNotifications;
